import { DatePipe } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { TranslocoDirective } from '@jsverse/transloco';

import { ErrorPage } from '../error-page';
import { Button } from '../../../../shared/components/button/button';

/** Resources with their own translated title; anything else gets the default. */
const KNOWN_RESOURCES = new Set(['organization', 'device']);

@Component({
  selector: 'app-not-found',
  imports: [DatePipe, TranslocoDirective, ErrorPage, Button],
  template: `
    <ng-container *transloco="let t; read: 'errors.notFound'">
      <app-error-page
        code="404"
        [title]="t('title.' + resourceKey())"
        [body]="t('body')"
        [rows]="[
          {
            label: t('rows.requested'),
            value: customResource() ?? t('resource.' + resourceKey()),
          },
          { label: t('rows.access'), value: t('rows.accessNone') },
          { label: t('rows.checked'), value: (checkedAt | date: checkedFormat) ?? '' },
        ]"
      >
        <app-button [label]="t('goHome')" (activated)="goHome()" />
      </app-error-page>
    </ng-container>
  `,
})
export class NotFoundPage {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  /** When the miss was established — rendered on the display's spec sheet. */
  protected readonly checkedAt = new Date();
  protected readonly checkedFormat = "EEE MMM d '·' HH:mm";

  private readonly resourceParam = toSignal(
    this.route.queryParams.pipe(map((params) => params['resource'] as string | undefined)),
  );

  /** Whitelisted translation-key suffix — never feed a raw URL param into a
   *  translation key. */
  protected readonly resourceKey = computed(() => {
    const resource = this.resourceParam();
    return resource && KNOWN_RESOURCES.has(resource) ? resource : 'default';
  });

  /** Any non-whitelisted resource still surfaces in the "Requested" row — as
   *  plain rendered text (safe to echo), clamped so garbage can't wreck the
   *  layout. Whitelisted ones use their translated label instead. */
  protected readonly customResource = computed(() => {
    const resource = this.resourceParam()?.trim();
    if (!resource || KNOWN_RESOURCES.has(resource)) return null;
    return resource.length > 40 ? resource.slice(0, 40) + '…' : resource;
  });

  goHome(): void {
    this.router.navigate(['/']);
  }
}
